import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Badge,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PlacingStatusBadge from "./PlacingStatusBadge";
import PartyName from "./PartyName";
import QuoteRequestForm from "./QuoteRequestForm";
import QuoteComparison from "./QuoteComparison";
import OrderSection from "./OrderSection";
import PotentialLeadsPicker, { fullLeadList } from "./PotentialLeadsPicker";
import { usePartyNames } from "../../hooks/usePartyNames";
import { compareQuotes, getPlacement, setPotentialLeads, shareSubmission, updateSubmission } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import { RESPONSES, eligibleForQuoteRequest, formatDate, isSignedDown, requestCounts, toIsoUtc, toLocalInput } from "./placingUtils";
import NotesField from "./NotesField";
import { ReasonText } from "./ReasonFields";
import { displayStageCode } from "./placingBook";
import { CodifiedDataButton } from "../codify/PlacementCodify";

const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" color="gray.500">{label}</Text>
    <Box fontSize="sm">{children}</Box>
  </Box>
);

/**
 * Broker view of a submission: potential leads, share, quote requests,
 * compare and nominate, and the Order. section shows one part only
 * ("build", "quotation" or "order", for the placement overview's drawers);
 * without it everything is shown, one section after another.
 */
const BrokerSubmissionView = ({ submission, onChanged, section }) => {
  const shows = (part) => !section || section === part;
  const toast = useToast();
  const leadsModal = useDisclosure();
  const [quoteRequestFor, setQuoteRequestFor] = useState(null); // a lead's partyId
  const [chosenLeadIds, setChosenLeadIds] = useState([]);
  const [savingLeads, setSavingLeads] = useState(false);
  const [shareDate, setShareDate] = useState(toLocalInput(submission.responseRequiredByDate));
  const [notes, setNotes] = useState(submission.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  // Notes can change while the submission is a draft; sharing locks it
  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateSubmission({ ...submission, notes });
      toast({ title: "Notes saved", status: "success" });
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't save notes" });
    } finally {
      setSavingNotes(false);
    }
  };
  const [sharing, setSharing] = useState(false);
  const [comparison, setComparison] = useState(null);

  const leads = useMemo(() => submission.sharedWithParties || [], [submission.sharedWithParties]);
  const isShared = submission.status === "shared";
  // Every interested lead can be sent a quote request (even another one)
  const eligibleIds = eligibleForQuoteRequest(leads).map((l) => l.partyId);
  const sentCounts = requestCounts(comparison?.rows);
  const selectedLeads = leads.filter((l) => l.status === "selected");
  const leadNames = usePartyNames(selectedLeads.map((l) => l.partyId));

  // The placement, for its stage and whether it has been signed down
  const [placement, setPlacement] = useState(null);
  const closed = isSignedDown(placement);

  const loadComparison = useCallback(async () => {
    if (submission.placementId) {
      getPlacement(submission.placementId).then(setPlacement).catch(() => {});
    }
    if (!isShared) return;
    try {
      setComparison(await compareQuotes(submission._id));
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't load quotes" });
    }
  }, [isShared, submission._id, submission.placementId, toast]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  const refreshAll = async () => {
    await onChanged();
    await loadComparison();
  };

  const openLeadsModal = () => {
    setChosenLeadIds(leads.map((l) => l.partyId));
    leadsModal.onOpen();
  };

  const saveLeads = async () => {
    setSavingLeads(true);
    try {
      await setPotentialLeads(submission._id, fullLeadList(submission, chosenLeadIds));
      toast({ title: "Potential leads updated", status: "success" });
      leadsModal.onClose();
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't update potential leads" });
    } finally {
      setSavingLeads(false);
    }
  };

  const share = async () => {
    const names = selectedLeads.map((l) => leadNames[l.partyId] || "a potential lead").join(", ");
    if (!window.confirm(`Share with ${selectedLeads.length} potential lead(s)?\n\n${names}`)) return;
    setSharing(true);
    try {
      // Sharing locks the submission, so keep any unsaved notes first
      if (notes !== (submission.notes || "")) await updateSubmission({ ...submission, notes });
      await shareSubmission(submission._id, toIsoUtc(shareDate));
      toast({ title: "Submission shared", status: "success" });
      await refreshAll();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't share submission" });
    } finally {
      setSharing(false);
    }
  };


  return (
    <VStack align="stretch" spacing={6}>
      {shows("build") && <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <HStack justify="space-between" mb={3}>
          <Heading size="md">{submission.name || "Submission"}</Heading>
          <HStack>
            {isShared && (
              <CodifiedDataButton placementId={submission.placementId} message={{ trigger: "submission", triggerId: submission._id }} size="xs" />
            )}
            <PlacingStatusBadge status={submission.status} />
            {isShared && <Text fontSize="xs" color="gray.500">Locked: shared submissions can't be edited</Text>}
          </HStack>
        </HStack>
        <SimpleGrid columns={[1, 2, 4]} spacing={3}>
          <Field label="Stage">{displayStageCode(submission.placingStageCode, (comparison?.rows || []).length > 0)}</Field>
          <Field label="Product">{submission.productCode || "—"}</Field>
          <Field label="Sender"><PartyName partyId={submission.sender?.partyId} /></Field>
          <Field label="Response required by">{formatDate(submission.responseRequiredByDate) || "—"}</Field>
          {submission.documentId && (
            <Field label="Contract document">
              <Link as={RouterLink} color="blue.600" to={`/editor?doc=${submission.documentId}`}>
                Open document
              </Link>
            </Field>
          )}
        </SimpleGrid>
        <Box mt={3}>
          <Text fontSize="xs" color="gray.500">Notes for the market</Text>
          {isShared ? (
            <Text fontSize="sm" whiteSpace="pre-wrap">{submission.notes || "—"}</Text>
          ) : (
            <>
              <NotesField label={null} value={notes} onChange={setNotes} placeholder="What the carriers should know about this risk" />
              <Button size="xs" mt={2} onClick={saveNotes} isLoading={savingNotes} isDisabled={notes === (submission.notes || "")}>
                Save notes
              </Button>
            </>
          )}
        </Box>
      </Box>}

      {shows("build") && <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <HStack justify="space-between" mb={3}>
          <Heading size="sm">Potential leads</Heading>
          {/* Once signed down the risk is closed: no more leads, shares or requests */}
          {!closed && <Button size="sm" onClick={openLeadsModal}>Select potential leads</Button>}
        </HStack>
        {leads.length === 0 ? (
          <Text fontSize="sm" color="gray.500">No potential leads selected yet.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Party</Th><Th>Status</Th><Th>Sent</Th><Th>Viewed</Th><Th>Response</Th><Th /></Tr>
            </Thead>
            <Tbody>
              {leads.map((lead) => (
                <Tr key={lead.partyId}>
                  <Td><PartyName partyId={lead.partyId} /></Td>
                  <Td><PlacingStatusBadge status={lead.status} /></Td>
                  <Td>{formatDate(lead.sentDate)}</Td>
                  <Td>{formatDate(lead.viewedDate) || (lead.status === "sent" ? <Text as="span" color="gray.400">Not opened</Text> : "")}</Td>
                  <Td>
                    {RESPONSES[lead.responseCode] && (
                      <Badge colorScheme={RESPONSES[lead.responseCode].colour} mr={2}>{RESPONSES[lead.responseCode].label}</Badge>
                    )}
                    {formatDate(lead.respondedDate || lead.declinedDate)}
                    {lead.status === "declined" ? (
                      <ReasonText color="red.600" code={lead.reasonCode} notes={lead.responseNotes || lead.declineReason} />
                    ) : (
                      (lead.responseNotes || lead.declineReason) && (
                        <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap">{lead.responseNotes || lead.declineReason}</Text>
                      )
                    )}
                  </Td>
                  <Td textAlign="right">
                    <HStack justify="flex-end" spacing={2}>
                      {sentCounts[lead.partyId] > 0 && (
                        <Text fontSize="xs" color="gray.500">
                          {sentCounts[lead.partyId]} request{sentCounts[lead.partyId] > 1 ? "s" : ""}
                        </Text>
                      )}
                      {isShared && !closed && eligibleIds.includes(lead.partyId) && (
                        <Button size="xs" colorScheme="blue" variant="outline" onClick={() => setQuoteRequestFor(lead.partyId)}>
                          Send quote request
                        </Button>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {!closed && <HStack mt={4} align="flex-end">
          <FormControl maxW="260px">
            <FormLabel fontSize="xs">Response required by (optional)</FormLabel>
            <Input size="sm" type="datetime-local" value={shareDate} onChange={(e) => setShareDate(e.target.value)} />
          </FormControl>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={share}
            isLoading={sharing}
            isDisabled={selectedLeads.length === 0}>
            Share with {selectedLeads.length} selected
          </Button>
        </HStack>}
      </Box>}

      {section && section !== "build" && !isShared && (
        <Text fontSize="sm" color="gray.500">Share the submission with potential leads first (in Build).</Text>
      )}
      {isShared && (
        <>
          {shows("quotation") && (
            <>
              {!closed && <QuoteRequestForm submission={submission} comparison={comparison} onSent={refreshAll} />}
              <QuoteComparison submissionId={submission._id} placementId={submission.placementId} comparison={comparison} onChanged={refreshAll} leads={leads} isClosed={closed} />
            </>
          )}
          {shows("order") && (
            <OrderSection
              submission={submission}
              placement={placement}
              comparison={comparison}
              onChanged={refreshAll}
              {...(section === "order" && { showSigning: false, defaultOpen: true })}
            />
          )}
        </>
      )}

      <Modal isOpen={!!quoteRequestFor} onClose={() => setQuoteRequestFor(null)} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send quote request to <PartyName partyId={quoteRequestFor} /></ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {quoteRequestFor && (
              <QuoteRequestForm
                submission={submission}
                comparison={comparison}
                defaultPartyIds={[quoteRequestFor]}
                onSent={async () => {
                  setQuoteRequestFor(null);
                  await refreshAll();
                }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={leadsModal.isOpen} onClose={leadsModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select potential leads</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <PotentialLeadsPicker submission={submission} value={chosenLeadIds} onChange={setChosenLeadIds} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={leadsModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={saveLeads} isLoading={savingLeads}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default BrokerSubmissionView;
